/**
 * PlaceholdersTab — the "Placeholders" tab of the in-editor agent panel.
 *
 * Author mode (the open file is a template): define placeholders (key,
 * label, type, required, default, options), save them to the docs backend,
 * and drop each one into the document at the caret as a Writer bookmark
 * `zph_<key>__<nn>` through the branding.js bridge. The list also shows
 * where each placeholder is placed (read back from the document), lets the
 * author jump to a placement, and surfaces orphan bookmarks (placed but
 * no longer defined).
 *
 * Fill mode (the open file was created from a template): one input per
 * placeholder; Apply writes the CHANGED values into every placement via
 * .uno:UpdateBookmarks, then records them on the document so the form comes
 * back filled next time. The document is the truth — only values that
 * differ from what was last recorded are written, so a value someone edited
 * directly in the text is never stomped by a stale one.
 *
 * Values are plain text end to end; branding.js is the only producer of
 * the markup the editor pastes.
 *
 * Filling from another module's records (backend docs/template-data.md): in
 * author mode a template names its record ("Fill from": e.g. a zehn-hr
 * employee) and each placeholder may be bound to one of that record's fields,
 * or to docs' own registration number/date. In fill mode a document made for
 * such a record can "Refill from" it: the module's current values land in the
 * form, and Apply writes them like any other change (never behind the open
 * editor's back).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle, ArrowDown, ArrowUp, Check, Crosshair, DownloadCloud, Link2, Loader2, Pencil, Plus, RefreshCw, RotateCcw,
  Trash2, Unlink, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useGetRequest, usePutRequest } from '@/shared/hooks/requests.js';
import { apiErrorMessage } from '@/shared/utils/apiError.js';
import {
  DOCS_BINDINGS, boundToSource, fetchTemplateSources, resolveSource, sourceApi, sourceLang, splitBinding, splitEntity,
} from '@/shared/utils/templateSources.js';
import { FILE_PLACEHOLDERS, FILE_PLACEHOLDER_PLACEMENTS, FILE_PLACEHOLDER_VALUES, forFile } from '../api.js';
import {
  MAX_DATE_YEAR,
  MAX_LABEL_LEN,
  MAX_VALUE_LEN,
  MIN_DATE_YEAR,
  TYPES,
  bookmarkName,
  describeExtent,
  extentIssue,
  groupExtents,
  groupPlacements,
  nextIndex,
  parseOptions,
  slugify,
  uniqueKey,
  validateDefinition,
  validateValue,
} from '../placeholders/bookmarks.js';

const INPUT = 'h-7 w-full min-w-0 rounded-[4px] border border-border bg-card px-2 text-[11.5px] text-foreground outline-none focus:border-brand disabled:opacity-60';
const TEXTAREA = 'w-full min-w-0 rounded-[4px] border border-border bg-card px-2 py-1 text-[11.5px] text-foreground outline-none focus:border-brand disabled:opacity-60';
const BTN = 'inline-flex h-7 flex-none items-center gap-1 rounded-[4px] border border-border bg-card px-2 text-[11px] font-medium text-foreground hover:bg-muted/60 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer';
const BTN_PRIMARY = 'inline-flex h-7 flex-none items-center gap-1 rounded-[4px] bg-brand px-2.5 text-[11px] font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer';
const ICON_BTN = 'flex h-6 w-6 flex-none items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer';
const BRIDGE_REASONS = new Set([
  'not_edit_mode', 'tracked_changes_on', 'not_ready', 'timeout', 'count_mismatch',
  'not_inserted', 'not_removed', 'bad_name', 'bad_key', 'error', 'ambiguous_name', 'not_found',
]);

/** API row (camelized by axios) → the local definition shape. */
function toDef(item) {
  return {
    key: item.key,
    label: item.label,
    type: item.type || 'text',
    required: Boolean(item.required),
    defaultValue: item.defaultValue ?? '',
    options: Array.isArray(item.options) ? item.options : [],
    value: item.value ?? null,
    binding: item.binding ?? null,
  };
}

function toApi(def) {
  return {
    key: def.key,
    label: def.label,
    type: def.type,
    required: def.required,
    defaultValue: def.defaultValue || null,
    options: def.type === 'choice' ? def.options : [],
    binding: def.binding || null,
  };
}

/**
 * Records the template can be about: every entity of every module the
 * install fills templates from (read through docs' pass-through, as the
 * person) — [{value: 'zehn-hr:employee', source, label, fields: [{path, label, type}]}].
 */
function useSourceEntities(lang) {
  const [state, setState] = useState({ loaded: false, entities: [] });
  useEffect(() => {
    let live = true;
    (async () => {
      const entities = [];
      for (const { source, apiUrl } of await fetchTemplateSources()) {
        const api = sourceApi(source, apiUrl);
        try {
          const catalogue = await api.catalogue(lang);
          for (const entity of catalogue?.entities || []) {
            entities.push({
              value: `${source}:${entity.key}`,
              source,
              label: entity.label || entity.key,
              fields: Array.isArray(entity.fields) ? entity.fields : [],
            });
          }
        } catch { /* that module stays out of the list */ }
      }
      if (live) setState({ loaded: true, entities });
    })();
    return () => { live = false; };
  }, [lang]);
  return state;
}

/** Where one placeholder's value comes from: typed, a field of the record, or the registration. */
function BindingSelect({ def, entity, onChange }) {
  const { t } = useTranslation('assistant');
  const fields = entity?.fields || [];
  const known = new Set([
    ...fields.map((f) => `${entity.source}:${f.path}`),
    ...DOCS_BINDINGS,
  ]);
  return (
    <label className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground">
      <Link2 className="h-3 w-3 flex-none" />
      <select
        className={`${INPUT} h-6`}
        value={def.binding || ''}
        aria-label={t('placeholders.source.bindingLabel')}
        onChange={(e) => {
          const value = e.target.value || null;
          const field = fields.find((f) => `${entity?.source}:${f.path}` === value);
          // docs' registration number/date are text; a field brings its type
          onChange(value, DOCS_BINDINGS.includes(value) ? 'text' : field?.type || def.type);
        }}
      >
        <option value="">{t('placeholders.source.typed')}</option>
        {def.binding && !known.has(def.binding) && <option value={def.binding}>{def.binding}</option>}
        {fields.map((f) => (
          <option key={f.path} value={`${entity.source}:${f.path}`}>
            {f.label || f.path}{f.sensitive ? ' 🔒' : ''}
          </option>
        ))}
        <option value="docs:registration.number">{t('placeholders.source.registrationNumber')}</option>
        <option value="docs:registration.date">{t('placeholders.source.registrationDate')}</option>
      </select>
    </label>
  );
}

/** Toast the bridge's refusal reason (or a generic failure). */
function toastBridgeFailure(t, result, fallbackKey) {
  const reason = result?.detail?.[0]?.reason;
  if (result === null) {
    toast.error(t('placeholders.bridge.no_editor'));
  } else if (reason && BRIDGE_REASONS.has(reason)) {
    toast.error(t(`placeholders.bridge.${reason}`));
  } else {
    toast.error(t(fallbackKey));
  }
}

/** Where each key is placed in the open document, refreshed on demand. */
function usePlacements(editorBridge) {
  const [placements, setPlacements] = useState({});
  const [known, setKnown] = useState(false);
  const refresh = useCallback(async () => {
    if (!editorBridge) return null;
    const res = await editorBridge.listBookmarks();
    if (res?.ok) {
      setPlacements(groupPlacements(res.names));
      setKnown(true);
    }
    return res;
  }, [editorBridge]);
  const adopt = useCallback((names) => {
    if (Array.isArray(names)) {
      setPlacements(groupPlacements(names));
      setKnown(true);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return { placements, known, refresh, adopt };
}

/**
 * What each placement currently COVERS, parsed by the backend from the
 * saved bytes. Bookmarks follow edits: paste over a `[Label]` marker and the
 * mark wraps the whole paste, and the next fill replaces it all — the
 * editor cannot report that, the bytes can. Every read is preceded by a
 * save so the bytes are the open document.
 */
function useExtents(fileUuid, editorBridge) {
  const get = useGetRequest({ showError: false });
  const getRef = useRef(get);
  getRef.current = get;
  const [extents, setExtents] = useState({ byKey: {}, supported: null, malformed: [] });
  const refreshExtents = useCallback(async () => {
    if (editorBridge) await editorBridge.requestSave();
    const { success, response } = await getRef.current.request({
      url: forFile(FILE_PLACEHOLDER_PLACEMENTS, fileUuid),
    });
    if (!success || !response) return null;
    const next = {
      byKey: groupExtents(response.items),
      supported: response.supported !== false,
      malformed: Array.isArray(response.malformed) ? response.malformed : [],
    };
    setExtents(next);
    return next;
  }, [editorBridge, fileUuid]);
  return { extents, refreshExtents };
}

/** The keys among `keys` with a placement whose extent deserves a warning. */
function keysWithIssues(byKey, keys) {
  return keys.filter((key) => (byKey[key] || []).some((p) => extentIssue(p)));
}

/**
 * One placement's extent: quiet when it is a marker-sized span, amber when
 * it grew (long / across paragraphs). `actions` (author mode) offers the
 * repairs; fill mode just says what Apply will replace.
 */
function PlacementExtent({ placement, onJump, onReset, onUnlink, busy }) {
  const { t } = useTranslation('assistant');
  const issue = extentIssue(placement);
  const shown = describeExtent(placement.text);
  const preview = shown ? `“${shown}”` : t('placeholders.extent.empty');
  const tag = t('placeholders.extent.placement', { index: String(placement.index).padStart(2, '0') });
  if (!issue) {
    return (
      <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
        <span className="font-mono">{tag}</span>
        <span className="min-w-0 flex-1 truncate">{preview}</span>
        {onJump && (
          <button type="button" className={ICON_BTN} onClick={onJump} title={t('placeholders.locate')}>
            <Crosshair className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  }
  const why = issue === 'multiline'
    ? t('placeholders.extent.multiline', { count: placement.breaks + 1, chars: placement.chars })
    : t('placeholders.extent.long', { chars: placement.chars });
  return (
    <div className="rounded-[6px] border border-amber-500/40 bg-amber-500/5 px-2 py-1 text-[10px]" data-extent-issue={issue}>
      <div className="flex items-center gap-1 font-semibold text-amber-700">
        <AlertTriangle className="h-3 w-3 flex-none" />
        <span className="font-mono">{tag}</span>
        <span className="min-w-0 flex-1">{why}</span>
      </div>
      <p className="mt-0.5 break-words text-foreground/80">{preview}</p>
      <div className="mt-1 flex items-center gap-1">
        {onJump && (
          <button type="button" className={ICON_BTN} onClick={onJump} title={t('placeholders.locate')}>
            <Crosshair className="h-3 w-3" />
          </button>
        )}
        {onUnlink ? (
          <button type="button" className={BTN} onClick={onUnlink} disabled={busy} title={t('placeholders.extent.unlinkHint')}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Unlink className="h-3 w-3" />}
            {t('placeholders.extent.unlink')}
          </button>
        ) : null}
        {onReset ? (
          <button type="button" className={BTN} onClick={onReset} disabled={busy} title={t('placeholders.extent.resetHint')}>
            <RotateCcw className="h-3 w-3" />
            {t('placeholders.extent.reset')}
          </button>
        ) : null}
        {!onUnlink && !onReset && <span className="text-amber-700">{t('placeholders.extent.willReplace')}</span>}
      </div>
    </div>
  );
}

function PlacementBadge({ count, known }) {
  const { t } = useTranslation('assistant');
  if (!known) return null;
  if (count > 0) {
    return (
      <span className="flex-none rounded-full bg-green-500/10 px-1.5 py-0.5 text-[9.5px] font-bold text-green-700">
        {t('placeholders.placed', { count })}
      </span>
    );
  }
  return (
    <span className="flex-none rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[9.5px] font-bold text-amber-700">
      {t('placeholders.notPlaced')}
    </span>
  );
}

// ---------------------------------------------------------------- author

function DefinitionForm({ initial, existingKeys, lockKey, onSave, onCancel }) {
  const { t } = useTranslation('assistant');
  const [label, setLabel] = useState(initial?.label || '');
  const [key, setKey] = useState(initial?.key || '');
  const [keyTouched, setKeyTouched] = useState(Boolean(initial?.key));
  const [type, setType] = useState(initial?.type || 'text');
  const [required, setRequired] = useState(Boolean(initial?.required));
  const [defaultValue, setDefaultValue] = useState(initial?.defaultValue || '');
  const [optionsText, setOptionsText] = useState((initial?.options || []).join('\n'));
  const [error, setError] = useState(null);

  const onLabel = (value) => {
    setLabel(value);
    if (!keyTouched && !lockKey) setKey(uniqueKey(slugify(value), existingKeys));
  };

  const submit = (e) => {
    e.preventDefault();
    const options = parseOptions(optionsText);
    const def = {
      key: key.trim(),
      label: label.trim(),
      type,
      required,
      defaultValue: defaultValue.trim(),
      options: type === 'choice' ? options : [],
    };
    const problem = validateDefinition(def, existingKeys);
    if (problem) { setError(problem); return; }
    onSave(def);
  };

  return (
    <form onSubmit={submit} className="space-y-1.5 rounded-[6px] border border-brand/40 bg-muted/30 p-2">
      <label className="block text-[10px] font-medium text-muted-foreground">
        {t('placeholders.label')}
        <input className={INPUT} value={label} onChange={(e) => onLabel(e.target.value)} autoFocus required maxLength={MAX_LABEL_LEN} />
      </label>
      <label className="block text-[10px] font-medium text-muted-foreground">
        {t('placeholders.key')}
        <input
          className={`${INPUT} font-mono`}
          value={key}
          disabled={lockKey}
          onChange={(e) => { setKeyTouched(true); setKey(e.target.value.toLowerCase()); }}
          maxLength={24}
        />
        <span className="block pt-0.5 text-[9.5px] font-normal">{t('placeholders.keyHint')}</span>
      </label>
      <div className="flex gap-1.5">
        <label className="block min-w-0 flex-1 text-[10px] font-medium text-muted-foreground">
          {t('placeholders.type')}
          <select className={INPUT} value={type} onChange={(e) => setType(e.target.value)}>
            {TYPES.map((value) => (
              <option key={value} value={value}>{t(`placeholders.types.${value}`)}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-none items-end gap-1 pb-1.5 text-[10.5px] text-foreground">
          <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
          {t('placeholders.required')}
        </label>
      </div>
      {type === 'choice' && (
        <label className="block text-[10px] font-medium text-muted-foreground">
          {t('placeholders.options')}
          <textarea className={TEXTAREA} rows={3} value={optionsText} onChange={(e) => setOptionsText(e.target.value)} />
          <span className="block pt-0.5 text-[9.5px] font-normal">{t('placeholders.optionsHint')}</span>
        </label>
      )}
      <label className="block text-[10px] font-medium text-muted-foreground">
        {t('placeholders.defaultValue')}
        <ValueInput def={{ type, options: parseOptions(optionsText) }} value={defaultValue} onChange={setDefaultValue} />
      </label>
      {error && (
        <p className="text-[10.5px] text-red-600">{t(`placeholders.errors.${error}`)}</p>
      )}
      <div className="flex justify-end gap-1.5 pt-0.5">
        <button type="button" className={BTN} onClick={onCancel}>
          <X className="h-3 w-3" /> {t('placeholders.cancel')}
        </button>
        <button type="submit" className={BTN_PRIMARY}>
          <Check className="h-3 w-3" /> {t('placeholders.ok')}
        </button>
      </div>
    </form>
  );
}

function AuthorView({ fileUuid, items, templateEntity, reload, editorBridge, onDocumentChanged }) {
  const { t, i18n } = useTranslation('assistant');
  const [defs, setDefs] = useState(() => items.map(toDef));
  const [dirty, setDirty] = useState(false);
  // The record this template is about (`zehn-hr:employee`), or null.
  const [entity, setEntity] = useState(templateEntity || null);
  const sources = useSourceEntities(sourceLang(null, i18n.language));
  const current = sources.entities.find((e) => e.value === entity) || null;
  // null | {index: number|null, initial, lockKey}
  const [editing, setEditing] = useState(null);
  const [confirmKey, setConfirmKey] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const { placements, known, refresh, adopt } = usePlacements(editorBridge);
  const { extents, refreshExtents } = useExtents(fileUuid, editorBridge);
  useEffect(() => { refreshExtents(); }, [refreshExtents]);
  const putDefs = usePutRequest({ showError: false });

  // The server list is the truth until the author starts editing locally.
  useEffect(() => { if (!dirty) setDefs(items.map(toDef)); }, [items]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!dirty) setEntity(templateEntity || null); }, [templateEntity]); // eslint-disable-line react-hooks/exhaustive-deps

  const url = forFile(FILE_PLACEHOLDERS, fileUuid);

  const persist = async (next) => {
    const { success, errorInfo } = await putDefs.request({ url, data: { items: next.map(toApi), entity } });
    if (!success) { toast.error(apiErrorMessage(errorInfo)); return false; }
    setDirty(false);
    reload();
    return true;
  };

  const save = async () => {
    if (await persist(defs)) toast.success(t('placeholders.toasts.saved'));
  };

  const commitForm = (def) => {
    const next = editing.index == null
      ? [...defs, def]
      : defs.map((d, i) => (i === editing.index ? { ...d, ...def } : d));
    setDefs(next);
    setDirty(true);
    setEditing(null);
  };

  // A new record kind drops the bindings to another module's fields.
  const changeEntity = (value) => {
    const source = splitEntity(value)?.source;
    setEntity(value);
    setDefs((prev) => prev.map((d) => {
      const bound = splitBinding(d.binding);
      return bound && !DOCS_BINDINGS.includes(d.binding) && bound.source !== source ? { ...d, binding: null } : d;
    }));
    setDirty(true);
  };

  const setBinding = (index, binding, type) => {
    setDefs((prev) => prev.map((d, i) => (i === index ? { ...d, binding, type } : d)));
    setDirty(true);
  };

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= defs.length) return;
    const next = [...defs];
    [next[index], next[target]] = [next[target], next[index]];
    setDefs(next);
    setDirty(true);
  };

  const insert = async (def) => {
    if (!editorBridge) return;
    if (dirty) { toast.error(t('placeholders.toasts.unsavedDefinition')); return; }
    setBusyKey(def.key);
    try {
      const at = await editorBridge.bookmarkAtCaret();
      if (at?.ok && at.name) { toast.error(t('placeholders.toasts.insertNested')); return; }
      const names = Object.values(placements).flat();
      const index = nextIndex(names, def.key);
      if (index == null) { toast.error(t('placeholders.toasts.tooManyPlacements')); return; }
      const res = await editorBridge.insertBookmark({
        name: bookmarkName(def.key, index),
        text: `[${def.label}]`,
      });
      if (!res?.ok) { toastBridgeFailure(t, res, 'placeholders.toasts.insertFailed'); return; }
      adopt(res.names);
      // Anchors must never trail the definitions on a quick close (the
      // extent refresh saves first).
      await refreshExtents();
      onDocumentChanged?.();
    } finally {
      setBusyKey(null);
    }
  };

  // Repairs for a placement that grew over pasted text: unlink keeps the
  // text and drops the mark (re-insert a fresh marker afterwards); reset
  // rewrites the mark back to its `[Label]` marker.
  const unlinkPlacement = async (name, key) => {
    if (!editorBridge) return;
    setBusyKey(key);
    try {
      const res = await editorBridge.unlinkBookmark(name);
      if (!res?.ok) { toastBridgeFailure(t, res, 'placeholders.toasts.unlinkFailed'); return; }
      adopt(res.names);
      await refreshExtents();
      onDocumentChanged?.();
    } finally {
      setBusyKey(null);
    }
  };

  const resetPlacement = async (def, name) => {
    if (!editorBridge) return;
    setBusyKey(def.key);
    try {
      const res = await editorBridge.updateBookmark({ name, text: `[${def.label}]` });
      if (!res?.ok) { toastBridgeFailure(t, res, 'placeholders.toasts.resetFailed'); return; }
      adopt(res.names);
      await refreshExtents();
      onDocumentChanged?.();
    } finally {
      setBusyKey(null);
    }
  };

  const remove = async (def) => {
    setConfirmKey(null);
    setBusyKey(def.key);
    try {
      const placed = (placements[def.key] || []).length;
      if (placed && editorBridge) {
        const res = await editorBridge.deleteBookmarks(def.key);
        if (!res?.ok) { toastBridgeFailure(t, res, 'placeholders.toasts.deleteFailed'); return; }
        adopt(res.names);
      }
      const next = defs.filter((d) => d.key !== def.key);
      setDefs(next);
      if (!(await persist(next))) { setDirty(true); return; }
      if (placed && editorBridge) {
        await refreshExtents();
        onDocumentChanged?.();
      }
    } finally {
      setBusyKey(null);
    }
  };

  const removeOrphan = async (key) => {
    if (!editorBridge) return;
    setBusyKey(key);
    try {
      const res = await editorBridge.deleteBookmarks(key);
      if (!res?.ok) { toastBridgeFailure(t, res, 'placeholders.toasts.deleteFailed'); return; }
      adopt(res.names);
      await refreshExtents();
      onDocumentChanged?.();
    } finally {
      setBusyKey(null);
    }
  };

  const locate = (key) => {
    const first = (placements[key] || [])[0];
    if (first && editorBridge) editorBridge.jumpToBookmark(first);
  };

  const definedKeys = defs.map((d) => d.key);
  const orphans = Object.keys(placements).filter((k) => !definedKeys.includes(k));

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="space-y-2 p-3">
        <p className="text-[11px] leading-relaxed text-muted-foreground">{t('placeholders.authorHint')}</p>
        <p className="text-[10.5px] leading-relaxed text-muted-foreground">{t('placeholders.extent.authorNote')}</p>
        <div className="space-y-1 rounded-[6px] border border-border bg-card p-2" data-testid="fill-from">
          <label className="block text-[10px] font-medium text-muted-foreground">
            {t('placeholders.source.fillFrom')}
            <select className={INPUT} value={entity || ''} onChange={(e) => changeEntity(e.target.value || null)}>
              <option value="">{t('placeholders.source.nothing')}</option>
              {entity && !current && <option value={entity}>{entity}</option>}
              {sources.entities.map((e) => (
                <option key={e.value} value={e.value}>{`${e.label} (${e.source})`}</option>
              ))}
            </select>
          </label>
          <p className="text-[9.5px] leading-relaxed text-muted-foreground">
            {sources.loaded && sources.entities.length === 0 ? t('placeholders.source.noneAvailable') : t('placeholders.source.fillFromHint')}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            className={BTN}
            onClick={() => setEditing({ index: null, initial: null, lockKey: false })}
            disabled={editing != null}
          >
            <Plus className="h-3 w-3" /> {t('placeholders.add')}
          </button>
          <button type="button" className={ICON_BTN} onClick={() => { refresh(); refreshExtents(); }} title={t('placeholders.refresh')} disabled={!editorBridge}>
            <RefreshCw className="h-3 w-3" />
          </button>
          <div className="flex-1" />
          <button type="button" className={BTN_PRIMARY} onClick={save} disabled={!dirty || putDefs.loading}>
            {putDefs.loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            {t('placeholders.save')}
          </button>
        </div>

        {editing != null && editing.index == null && (
          <DefinitionForm
            initial={editing.initial}
            existingKeys={definedKeys}
            lockKey={editing.lockKey}
            onSave={commitForm}
            onCancel={() => setEditing(null)}
          />
        )}

        {defs.length === 0 && editing == null && (
          <p className="rounded-[6px] border border-dashed border-border p-3 text-center text-[11px] text-muted-foreground">
            {t('placeholders.empty')}
          </p>
        )}

        <ul className="space-y-1.5">
          {defs.map((def, index) => {
            const count = (placements[def.key] || []).length;
            const busy = busyKey === def.key;
            if (editing != null && editing.index === index) {
              return (
                <li key={def.key}>
                  <DefinitionForm
                    initial={def}
                    existingKeys={definedKeys.filter((k) => k !== def.key)}
                    lockKey
                    onSave={commitForm}
                    onCancel={() => setEditing(null)}
                  />
                </li>
              );
            }
            return (
              <li key={def.key} className="rounded-[6px] border border-border bg-card p-2">
                <div className="flex items-center gap-1.5">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1 text-[11.5px] font-semibold text-foreground">
                      <span className="truncate">{def.label}</span>
                      {def.required && <span className="text-red-500">*</span>}
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                      <span className="font-mono">{def.key}</span>
                      <span>·</span>
                      <span>{t(`placeholders.types.${def.type}`)}</span>
                    </div>
                  </div>
                  <PlacementBadge count={count} known={known} />
                </div>
                <BindingSelect def={def} entity={current} onChange={(binding, type) => setBinding(index, binding, type)} />
                <div className="mt-1.5 flex items-center gap-1">
                  <button
                    type="button"
                    className={BTN}
                    onClick={() => insert(def)}
                    disabled={!editorBridge || busy || dirty}
                    title={dirty ? t('placeholders.toasts.unsavedDefinition') : undefined}
                  >
                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                    {t('placeholders.insert')}
                  </button>
                  {count > 0 && (
                    <button type="button" className={ICON_BTN} onClick={() => locate(def.key)} title={t('placeholders.locate')}>
                      <Crosshair className="h-3 w-3" />
                    </button>
                  )}
                  <div className="flex-1" />
                  <button type="button" className={ICON_BTN} onClick={() => move(index, -1)} disabled={index === 0} title={t('placeholders.moveUp')}>
                    <ArrowUp className="h-3 w-3" />
                  </button>
                  <button type="button" className={ICON_BTN} onClick={() => move(index, 1)} disabled={index === defs.length - 1} title={t('placeholders.moveDown')}>
                    <ArrowDown className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    className={ICON_BTN}
                    onClick={() => setEditing({ index, initial: def, lockKey: true })}
                    disabled={editing != null}
                    title={t('placeholders.edit')}
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                  {confirmKey === def.key ? (
                    <span className="flex items-center gap-1 text-[10px]">
                      <button type="button" className="rounded bg-red-600 px-1.5 py-0.5 font-semibold text-white cursor-pointer" onClick={() => remove(def)}>
                        {t('placeholders.yes')}
                      </button>
                      <button type="button" className="rounded border border-border px-1.5 py-0.5 cursor-pointer" onClick={() => setConfirmKey(null)}>
                        {t('placeholders.no')}
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className={`${ICON_BTN} hover:text-red-600`}
                      onClick={() => setConfirmKey(def.key)}
                      disabled={busy}
                      title={t('placeholders.remove')}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
                {confirmKey === def.key && (
                  <p className="mt-1 text-[10px] text-red-600">
                    {t('placeholders.confirmRemove', { label: def.label, count })}
                  </p>
                )}
                {(extents.byKey[def.key] || []).length > 0 && (
                  <div className="mt-1.5 space-y-1">
                    {extents.byKey[def.key].map((placement) => (
                      <PlacementExtent
                        key={placement.name}
                        placement={placement}
                        busy={busy}
                        onJump={editorBridge ? () => editorBridge.jumpToBookmark(placement.name) : null}
                        onUnlink={editorBridge ? () => unlinkPlacement(placement.name, def.key) : null}
                        onReset={editorBridge ? () => resetPlacement(def, placement.name) : null}
                      />
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {orphans.length > 0 && (
          <div className="space-y-1 pt-1">
            <p className="flex items-center gap-1 text-[10.5px] font-semibold text-amber-700">
              <AlertTriangle className="h-3 w-3" /> {t('placeholders.orphans')}
            </p>
            <p className="text-[10px] text-muted-foreground">{t('placeholders.orphansHint')}</p>
            {orphans.map((key) => (
              <div key={key} className="flex items-center gap-1.5 rounded-[6px] border border-amber-500/40 bg-amber-500/5 px-2 py-1">
                <span className="min-w-0 flex-1 truncate font-mono text-[10.5px]">{key}</span>
                <PlacementBadge count={(placements[key] || []).length} known={known} />
                <button
                  type="button"
                  className={BTN}
                  onClick={() => setEditing({ index: null, initial: { key, label: key, type: 'text' }, lockKey: true })}
                  disabled={editing != null}
                >
                  {t('placeholders.define')}
                </button>
                <button type="button" className={`${ICON_BTN} hover:text-red-600`} onClick={() => removeOrphan(key)} disabled={busyKey === key} title={t('placeholders.remove')}>
                  {busyKey === key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                </button>
              </div>
            ))}
          </div>
        )}

        {extents.malformed.length > 0 && (
          <div className="space-y-1 pt-1">
            <p className="flex items-center gap-1 text-[10.5px] font-semibold text-amber-700">
              <AlertTriangle className="h-3 w-3" /> {t('placeholders.extent.malformed')}
            </p>
            <p className="text-[10px] text-muted-foreground">{t('placeholders.extent.malformedHint')}</p>
            {extents.malformed.map((name) => (
              <div key={name} className="flex items-center gap-1.5 rounded-[6px] border border-amber-500/40 bg-amber-500/5 px-2 py-1">
                <span className="min-w-0 flex-1 truncate font-mono text-[10.5px]">{name}</span>
                <button type="button" className={BTN} onClick={() => unlinkPlacement(name, name)} disabled={!editorBridge || busyKey === name}>
                  {busyKey === name ? <Loader2 className="h-3 w-3 animate-spin" /> : <Unlink className="h-3 w-3" />}
                  {t('placeholders.extent.unlink')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ fill

function ValueInput({ def, value, onChange, disabled, invalid }) {
  const { t } = useTranslation('assistant');
  const cls = `${INPUT} ${invalid ? 'border-red-500' : ''}`;
  switch (def.type) {
    case 'multiline':
      return <textarea className={`${TEXTAREA} ${invalid ? 'border-red-500' : ''}`} rows={3} value={value} maxLength={MAX_VALUE_LEN} aria-invalid={invalid || undefined} onChange={(e) => onChange(e.target.value)} disabled={disabled} />;
    case 'number':
      // Text, not type=number: a decimal comma ("12,5") is a number here, and
      // the browser's number input would drop it.
      return <input className={cls} type="text" inputMode="decimal" value={value} maxLength={64} aria-invalid={invalid || undefined} onChange={(e) => onChange(e.target.value)} disabled={disabled} />;
    case 'date':
      return <input className={cls} type="date" min={`${MIN_DATE_YEAR}-01-01`} max={`${MAX_DATE_YEAR}-12-31`} value={value} aria-invalid={invalid || undefined} onChange={(e) => onChange(e.target.value)} disabled={disabled} />;
    case 'choice':
      return (
        <select className={cls} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
          <option value="">{t('placeholders.choose')}</option>
          {(def.options || []).map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    default:
      return <input className={cls} type="text" value={value} maxLength={MAX_VALUE_LEN} aria-invalid={invalid || undefined} onChange={(e) => onChange(e.target.value)} disabled={disabled} />;
  }
}

/** A placement still holding its "[Label]" marker (the text a fill replaces). */
const isMarker = (text) => /^\[[^\]]*\]$/.test(String(text ?? '').replace(/\u00a0/g, ' ').trim());

/** What the document holds per key (last recorded value, '' when none). */
function recordedMap(items) {
  const map = {};
  for (const item of items) map[item.key] = item.value ?? '';
  return map;
}

function FillView({ fileUuid, items, sourceRef, templateLanguage, reload, editorBridge, onDocumentChanged }) {
  const { t, i18n } = useTranslation('assistant');
  const defs = items.map(toDef);
  // Placeholders filled from the record this document was made for.
  const bound = sourceRef ? boundToSource(items, sourceRef.source) : [];
  const [refilling, setRefilling] = useState(false);
  // Form state: last recorded value, else the template's default.
  const [values, setValues] = useState(() => {
    const map = {};
    for (const item of items) map[item.key] = item.value ?? item.defaultValue ?? '';
    return map;
  });
  const [recorded, setRecorded] = useState(() => recordedMap(items));
  const [errors, setErrors] = useState({});
  const [applying, setApplying] = useState(false);
  // Keys whose placements hold more than a marker, awaiting the person's
  // go-ahead before Apply overwrites that text.
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const { placements, known, refresh } = usePlacements(editorBridge);
  const { extents, refreshExtents } = useExtents(fileUuid, editorBridge);
  useEffect(() => { refreshExtents(); }, [refreshExtents]);
  const putValues = usePutRequest({ showError: false });
  const seenRef = useRef(new Set(items.map((i) => i.key)));

  // A reload after Apply brings the recorded values; keys that appeared
  // since (never for a snapshot, but cheap) get their initial value.
  useEffect(() => {
    setRecorded(recordedMap(items));
    setValues((prev) => {
      const next = { ...prev };
      for (const item of items) {
        if (!seenRef.current.has(item.key)) {
          seenRef.current.add(item.key);
          next[item.key] = item.value ?? item.defaultValue ?? '';
        }
      }
      return next;
    });
  }, [items]);

  const dirtyKeys = defs.filter((d) => (values[d.key] ?? '') !== (recorded[d.key] ?? '')).map((d) => d.key);
  // Optional fields left empty still show their "[Label]" marker in the
  // document. Finish filling blanks those markers through the editor, like
  // Apply, so the document reads as final — once every required field is in.
  const markerKeys = defs
    .filter((d) => !d.required && !String(values[d.key] ?? '').trim()
      && (extents.byKey[d.key] || []).some((p) => isMarker(p.text)))
    .map((d) => d.key);
  const requiredEmpty = defs.filter((d) => d.required && !String(values[d.key] ?? '').trim());
  // Keys a confirmed Apply writes besides the changed ones (Finish filling).
  const pendingExtraRef = useRef([]);

  const apply = async (force = false, extra = []) => {
    const problems = {};
    for (const def of defs) {
      const problem = validateValue(def, values[def.key], { strict: true });
      if (problem) problems[def.key] = problem;
    }
    setErrors(problems);
    if (Object.keys(problems).length) return;
    const keys = [...new Set([...dirtyKeys, ...extra])];
    if (!keys.length) return;
    setApplying(true);
    try {
      // The guard that makes the reported data loss impossible: a placement
      // that grew over document text is named, and Apply waits for a yes.
      if (editorBridge && !force) {
        const fresh = await refreshExtents();
        // Fail closed: a guard that could not look is no guard. (A format
        // the parser does not read — supported:false — is not that case.)
        if (!fresh && extents.supported === null) { toast.error(t('placeholders.toasts.extentsUnavailable')); return; }
        const offenders = keysWithIssues((fresh || extents).byKey, keys);
        if (offenders.length) { pendingExtraRef.current = extra; setPendingConfirm(offenders); return; }
      }
      setPendingConfirm(null);
      pendingExtraRef.current = [];
      if (editorBridge) {
        const res = await editorBridge.fillBookmarks(keys.map((key) => ({ key, text: values[key] ?? '' })));
        if (!res?.ok) { toastBridgeFailure(t, res, 'placeholders.toasts.fillFailed'); return; }
        if (res.missing?.length) toast.warning(t('placeholders.toasts.missingInDoc', { count: res.missing.length }));
      }
      const { success, errorInfo, response } = await putValues.request({
        url: forFile(FILE_PLACEHOLDER_VALUES, fileUuid),
        data: { values: keys.map((key) => ({ key, value: values[key] ?? '' })), validateRequired: true },
      });
      if (!success) { toast.error(apiErrorMessage(errorInfo)); return; }
      setRecorded(recordedMap(response?.items || []));
      if (editorBridge) {
        await editorBridge.requestSave();
        onDocumentChanged?.();
      }
      toast.success(extra.length
        ? t('placeholders.toasts.finished', { count: extra.length })
        : t('placeholders.toasts.applied', { count: keys.length }));
      refresh();
      refreshExtents();
      reload();
    } finally {
      setApplying(false);
    }
  };

  const locate = (key) => {
    const first = (placements[key] || [])[0];
    if (first && editorBridge) editorBridge.jumpToBookmark(first);
  };

  // The record's current values into the form; Apply writes them through the
  // editor like any other change, so the open session never goes stale.
  const refill = async () => {
    setRefilling(true);
    try {
      const { api, reason } = await resolveSource(sourceRef.source);
      if (!api) {
        toast.error(t(`placeholders.source.unavailable.${reason}`, { source: sourceRef.source }));
        return;
      }
      const answer = await api.values(
        // values in the document's own language (dates, amounts, script)
        sourceRef.entity, sourceRef.id, [...new Set(bound.map((b) => b.path))], sourceLang(templateLanguage, i18n.language),
      );
      const got = answer?.values || {};
      const refused = new Set(answer?.denied || []);
      setValues((prev) => {
        const next = { ...prev };
        for (const { key, path } of bound) {
          if (!refused.has(path) && got[path] != null) next[key] = String(got[path]);
        }
        return next;
      });
      toast.success(t('placeholders.source.refilled', { source: sourceRef.source }));
    } catch {
      toast.error(t('placeholders.source.unavailable.failed', { source: sourceRef.source }));
    } finally {
      setRefilling(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="space-y-2 p-3">
        <p className="text-[11px] leading-relaxed text-muted-foreground">{t('placeholders.fillHint')}</p>
        {sourceRef && bound.length > 0 && (
          <div className="flex items-center gap-1.5 rounded-[6px] border border-border bg-card p-2" data-testid="refill">
            <span className="min-w-0 flex-1 truncate text-[10.5px] text-muted-foreground">
              {t('placeholders.source.madeFor', { label: sourceRef.label || sourceRef.id, source: sourceRef.source })}
            </span>
            <button type="button" className={BTN} onClick={refill} disabled={refilling || applying}>
              {refilling ? <Loader2 className="h-3 w-3 animate-spin" /> : <DownloadCloud className="h-3 w-3" />}
              {t('placeholders.source.refill', { source: sourceRef.source })}
            </button>
          </div>
        )}
        <div className="space-y-2">
          {defs.map((def) => {
            const count = (placements[def.key] || []).length;
            const dirty = dirtyKeys.includes(def.key);
            return (
              <div key={def.key} className={`rounded-[6px] border bg-card p-2 ${dirty ? 'border-brand/60' : 'border-border'}`}>
                <div className="mb-1 flex items-center gap-1.5">
                  <button
                    type="button"
                    className="min-w-0 flex-1 truncate text-left text-[11.5px] font-semibold text-foreground hover:text-brand cursor-pointer"
                    onClick={() => locate(def.key)}
                    title={count > 0 ? t('placeholders.locate') : undefined}
                  >
                    {def.label}
                    {def.required && <span className="text-red-500"> *</span>}
                  </button>
                  {known && count === 0 ? (
                    <span className="flex items-center gap-0.5 rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[9.5px] font-bold text-amber-700" title={t('placeholders.missingInDoc')}>
                      <AlertTriangle className="h-2.5 w-2.5" /> {t('placeholders.missingInDocShort')}
                    </span>
                  ) : (
                    <PlacementBadge count={count} known={known} />
                  )}
                </div>
                <ValueInput
                  def={def}
                  value={values[def.key] ?? ''}
                  onChange={(v) => {
                    setValues((prev) => ({ ...prev, [def.key]: v }));
                    if (errors[def.key]) setErrors((prev) => ({ ...prev, [def.key]: undefined }));
                  }}
                  disabled={applying}
                  invalid={Boolean(errors[def.key])}
                />
                {errors[def.key] && (
                  <p className="mt-0.5 text-[10px] text-red-600">{t(`placeholders.errors.${errors[def.key]}`)}</p>
                )}
                {(extents.byKey[def.key] || []).filter((p) => extentIssue(p)).map((placement) => (
                  <div key={placement.name} className="mt-1">
                    <PlacementExtent
                      placement={placement}
                      onJump={editorBridge ? () => editorBridge.jumpToBookmark(placement.name) : null}
                    />
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        {pendingConfirm && (
          <div className="rounded-[6px] border border-amber-500/50 bg-amber-500/10 p-2 text-[11px]" role="alertdialog">
            <p className="flex items-center gap-1 font-semibold text-amber-800">
              <AlertTriangle className="h-3 w-3" /> {t('placeholders.extent.confirmTitle', { count: pendingConfirm.length })}
            </p>
            <p className="mt-0.5 text-muted-foreground">
              {t('placeholders.extent.confirmBody', { keys: pendingConfirm.map((k) => defs.find((d) => d.key === k)?.label || k).join(', ') })}
            </p>
            <div className="mt-1.5 flex items-center gap-1.5">
              <button type="button" className={BTN_PRIMARY} onClick={() => apply(true, pendingExtraRef.current)} disabled={applying}>
                {t('placeholders.extent.confirmYes')}
              </button>
              <button type="button" className={BTN} onClick={() => setPendingConfirm(null)} disabled={applying}>
                {t('placeholders.cancel')}
              </button>
            </div>
          </div>
        )}
        <div className="flex items-center gap-1.5 pt-1">
          <button type="button" className={ICON_BTN} onClick={() => { refresh(); refreshExtents(); }} title={t('placeholders.refresh')} disabled={!editorBridge}>
            <RefreshCw className="h-3 w-3" />
          </button>
          <div className="flex-1" />
          {editorBridge && markerKeys.length > 0 && (
            <button
              type="button"
              className={BTN}
              data-testid="finish-filling"
              onClick={() => apply(false, markerKeys)}
              disabled={applying || requiredEmpty.length > 0}
              title={requiredEmpty.length > 0
                ? t('placeholders.finish.requiredFirst', { fields: requiredEmpty.map((d) => d.label).join(', ') })
                : t('placeholders.finish.hint', { count: markerKeys.length })}
            >
              <Check className="h-3 w-3" />
              {t('placeholders.finish.button')}
            </button>
          )}
          <button type="button" className={BTN_PRIMARY} onClick={() => apply(false)} disabled={applying || dirtyKeys.length === 0}>
            {applying ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            {t('placeholders.apply', { count: dirtyKeys.length })}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- tab

/**
 * @param {string} fileUuid — the open file
 * @param {'author'|'fill'} mode
 * @param items — placeholder rows from GET /file/{file_uuid}/placeholders (camelized)
 * @param sourceRef — the other module's record the document was made for
 *   ({source, entity, id, label}: `id` is that module's own reference)
 */
export function PlaceholdersTab({
  fileUuid, mode, items, templateEntity = null, sourceRef = null, templateLanguage = null,
  reload, editorBridge, onDocumentChanged,
}) {
  if (mode === 'author') {
    return (
      <AuthorView
        fileUuid={fileUuid}
        items={items}
        templateEntity={templateEntity}
        reload={reload}
        editorBridge={editorBridge}
        onDocumentChanged={onDocumentChanged}
      />
    );
  }
  return (
    <FillView
      fileUuid={fileUuid}
      items={items}
      sourceRef={sourceRef}
      templateLanguage={templateLanguage}
      reload={reload}
      editorBridge={editorBridge}
      onDocumentChanged={onDocumentChanged}
    />
  );
}
